import { Bell, CheckCheck, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { FaSignOutAlt, FaUserCircle } from "react-icons/fa";
import { FiMenu } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../../../context/AdminAuthContext";
import useIsDesktop from "../../../../hooks/useIsDesktop";
import { ADMIN_NAV_HEIGHT } from "../../../../utils/constants";
import axios from "axios";
import { useNotifications } from "../../../../context/NotificationContext";

const SuperAdminNavbar = ({ setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { isStaff } = useAdminAuth();
  const isDesktop = useIsDesktop();
  const profileBtnRef = useRef(null);
  const scrollRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationBtnRef = useRef(null);

  const { admin, adminRole, logoutAdmin, isAdmin } = useAdminAuth();
  const { notifications, unreadCount, setNotifications, setUnreadCount, setUnreadBookingIds } = useNotifications();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const adminId = admin?.id || admin?._id;

  const adminName = admin?.name || "User";
  const roleLabel = adminRole === "super_admin" && "super admin";

  const handleLogout = async () => {
    const isLoggedOut = await logoutAdmin();
    if (isLoggedOut) navigate("/admin-login");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // ─── API Calls ────────────────────────────────────────────────────────────

  const fetchNotifications = async (pageNum = 1, append = false) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notifications`, {
        params: {
          id: adminId,
          model: "AdminUser",
          page: pageNum,
          limit: 10,
        },
      });

      const newData = res.data.data || [];

      setNotifications((prev) =>
        append ? [...prev, ...newData] : newData
      );

      //  check if more pages exist
      const totalPages = res.data.pagination?.pages || 1;
      setHasMore(pageNum < totalPages);

      // unread logic (only first load)
      if (!append) {
        const unreadIds = newData
          .filter(n => !n.isRead && n?.data?.bookingId)
          .map(n => n.data.bookingId);

        setUnreadBookingIds(unreadIds);
      }

    } catch (err) {
      console.error("Fetch notifications error:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notifications/unread-count`, {
        params: {
          id: adminId,
          model: "AdminUser",
        },
      });
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      console.error("Fetch unread count error:", err);
    }
  };

  const markAllAsSeen = async () => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/notifications/mark-seen`, {
        id: adminId,
        model: "AdminUser",
      });
    } catch (err) {
      console.error("Mark seen error:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/notifications/mark-read`, {
        id: adminId,
        model: "AdminUser",
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Mark all read error:", err);
    }
  };

  const handleNotificationItemClick = async (notif) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/notifications/${notif._id}/read`, {
        id: adminId,
        model: "AdminUser",
      });

      setNotifications((prev) =>
        prev.map((n) => (n._id === notif._id ? { ...n, isRead: true } : n))
      );

      setUnreadBookingIds((prev) =>
        prev.filter((id) => id !== notif?.data?.bookingId)
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));

      setIsNotificationOpen(false);

      if (notif.redirectUrl) {
        navigate(`${notif.redirectUrl}?highlight=${notif.data?.bookingId}`);
      }
    } catch (err) {
      console.error("Notification click error:", err);
    }
  };

  const handleNotificationClick = async () => {
    const opening = !isNotificationOpen;
    setIsNotificationOpen(opening);

    if (opening) {
      setPage(1);
      fetchNotifications(1, false);
      markAllAsSeen();
    }
  };

  // ─── Click Outside Handlers ───────────────────────────────────────────────

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const isBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

      if (isBottom && hasMore && !loadingMore) {
        setPage((prevPage) => {
          const nextPage = prevPage + 1;
          fetchNotifications(nextPage, true);
          return nextPage;
        });
      }
    };

    el.addEventListener("scroll", handleScroll);

    return () => el.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close profile dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileBtnRef.current &&
        !profileBtnRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }

      // Close notification panel
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        notificationBtnRef.current &&
        !notificationBtnRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (adminId) {
      fetchUnreadCount();
    }
  }, [adminId]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const handleHamburgerClick = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const initials = adminName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const profileRoute = isStaff ? "/staff-profile" : "/admin-profile";

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getNotifIcon = (type) => {
    const icons = {
      BOOKING_CREATED: "📅",
      BOOKING_CONFIRMED: "✅",
      BOOKING_CANCELLED: "❌",
      BOOKING_RESCHEDULED: "🔄",
      JOB_ASSIGNED: "👷",
      PAYMENT_SUCCESS: "💰",
      PAYMENT_FAILED: "⚠️",
      SYSTEM: "🔔",
    };
    return icons[type] || "🔔";
  };

  const unreadNotifications = notifications.filter((n) => !n.isRead);

  if (isStaff && !isDesktop) return null;

  return (
    <React.Fragment>
      <nav
        style={{ height: `${ADMIN_NAV_HEIGHT}px` }}
        className="bg-[#FFFFFF] shadow-lg absolute right-0 top-0 left-0 flex-1 p-3 text-black z-50"
      >
        <div className="px-3 mx-auto flex justify-between items-center h-full">
          {/* Logo / Hamburger */}
          <div className="flex items-center">
            <button
              className="lg:hidden text-primaryTextColor"
              onClick={handleHamburgerClick}
              aria-label="Toggle sidebar"
            >
              <FiMenu size={22} />
            </button>
          </div>

          {/* Right Side: Bell + Profile */}
          <div className="flex items-center gap-4">
            {/* ── Notification Bell ── */}
            {isAdmin && (
              <div className="relative">
                <button
                  ref={notificationBtnRef}
                  onClick={handleNotificationClick}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition"
                  aria-label="Notifications"
                >
                  <Bell size={22} className="text-gray-700" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-[0.6rem] rounded-full h-5 w-5 flex items-center justify-center shadow-md animate-pulse font-bold">
                      {unreadCount}
                    </span>
                  )}

                </button>

                {/* ── Notification Panel ── */}
                {isNotificationOpen && (
                  <div
                    ref={notificationRef}
                    className="absolute right-0 top-12 w-80 sm:w-96 bg-white shadow-2xl rounded-2xl border border-gray-100 z-50 flex flex-col overflow-hidden" style={{ maxHeight: "480px" }}
                  >
                    {/* Panel Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <Bell size={16} className="text-gray-600" />
                        <span className="font-semibold text-sm text-gray-800">
                          Notifications
                        </span>
                        {
                          unreadCount > 0 &&
                          <span className="bg-red-500 text-white text-[0.6rem] font-bold rounded-full px-1.5 py-0.5 leading-tight">
                            {unreadCount}
                          </span>
                        }
                      </div>
                      <div className="flex items-center gap-1">
                        {unreadNotifications.length > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-lg transition font-medium"
                            title="Mark all as read"
                          >
                            <CheckCheck size={13} />
                            <span className="hidden sm:inline">Mark all read</span>
                          </button>
                        )}
                        <button
                          onClick={() => setIsNotificationOpen(false)}
                          className="p-1 rounded-lg hover:bg-gray-200 transition text-gray-500"
                        >
                          <X size={15} />
                        </button>
                      </div>
                    </div>

                    {/* Panel Body */}
                    <div
                      ref={scrollRef}
                      style={{ maxHeight: "400px" }}
                      className="overflow-y-auto ">
                      {loading ? (
                        <div className="flex flex-col items-center justify-center py-10 gap-2">
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          <p className="text-xs text-gray-400">Loading notifications...</p>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
                          <Bell size={28} className="opacity-30" />
                          <p className="text-sm font-medium">No notifications yet</p>
                          <p className="text-xs">You're all caught up!</p>
                        </div>
                      ) : (
                        <ul>
                          {notifications.map((notif, idx) => (
                            <li key={notif._id}>
                              <button
                                onClick={() => handleNotificationItemClick(notif)}
                                className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition group ${!notif.isRead ? "bg-blue-50/60" : "bg-white"
                                  } ${idx !== notifications.length - 1 ? "border-b border-gray-100" : ""}`}
                              >
                                {/* Icon */}
                                <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base ${!notif.isRead ? "bg-blue-100" : "bg-gray-100"
                                  }`}>
                                  {getNotifIcon(notif.type)}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className={`text-sm leading-snug truncate ${!notif.isRead ? "font-semibold text-gray-900" : "font-medium text-gray-700"
                                      }`}>
                                      {notif.title}
                                    </p>
                                    {!notif.isRead && (
                                      <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1" />
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                                    {notif.message}
                                  </p>
                                  <p className="text-[0.65rem] text-gray-400 mt-1 font-medium">
                                    {formatTime(notif.createdAt)}
                                  </p>
                                </div>
                              </button>
                            </li>
                          ))}
                          {loadingMore && (
                            <div className="flex justify-center py-3">
                              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Profile Dropdown ── */}
            <button
              ref={profileBtnRef}
              onClick={toggleDropdown}
              className="flex items-center gap-1"
              type="button"
            >
              <div className="relative">
                <svg width="0" height="0">
                  <linearGradient
                    id="icon-gradient"
                    x1="100%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop
                      stopColor="currentColor"
                      className="text-primaryColor"
                      offset="0%"
                    />
                    <stop
                      stopColor="currentColor"
                      className="text-primaryTextColor"
                      offset="100%"
                    />
                  </linearGradient>
                </svg>
                <div className="h-8 w-8 rounded-full overflow-hidden bg-gradient-to-br from-accentTextColor to-brandAccentColor flex items-center justify-center text-white font-semibold text-sm">
                  {admin?.profilePicture ? (
                    <img
                      src={admin.profilePicture}
                      alt={adminName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
              </div>

              <svg
                className={`w-2 h-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 4 4 4-4"
                />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-5 top-14 w-52 bg-white rounded-xl shadow-xl border border-gray-100 z-50 animate-fade-in"
              >
                <div className="border-b border-gray-100">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-primaryTextColor truncate">
                      {adminName}
                    </p>
                    <p className="text-xs text-primaryColor capitalize">
                      {roleLabel}
                    </p>
                  </div>
                </div>

                <ul className="text-sm py-2 text-primaryTextColor">
                  <li>
                    <Link
                      to={profileRoute}
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 gap-3 hover:bg-primaryFadedColor hover:text-accentTextColor hover:bg-brandAccentColor/30 transition"
                    >
                      <FaUserCircle size={16} />
                      Profile
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 gap-3 text-left hover:bg-red-50 text-red-600 transition"
                    >
                      <FaSignOutAlt size={16} />
                      Sign out
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </nav>
    </React.Fragment>
  );
};

export default SuperAdminNavbar;